import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  deleteInventoryStockBalance,
  saveInventoryStockBalance,
} from '@/renderer/services/inventoryStockBalanceService';
import { SaveInventoryStockBalanceRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveInventoryStockBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveInventoryStockBalanceRequest) => {
      const result = await saveInventoryStockBalance(payload);
      if (!result.success) {
        throw new Error(
          result.error ?? 'Failed to save inventory stock balance',
        );
      }

      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess(
        payload.id ? 'Stock balance updated.' : 'Stock balance saved.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useDeleteInventoryStockBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      outletId,
      balanceId,
    }: {
      companyId: number;
      outletId: string;
      balanceId: string;
    }) => {
      const result = await deleteInventoryStockBalance(
        companyId,
        outletId,
        balanceId,
      );
      if (!result.success) {
        throw new Error(
          result.error ?? 'Failed to delete inventory stock balance',
        );
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess('Stock balance deleted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}
